/**
 * 全国行政区划数据（省 / 市 / 区县 三级 + 中心点经纬度）
 *
 * 用途：新建 / 编辑项目时「省 → 市 → 区县」三级联动，并据所选行政区
 *       自动带出地图坐标（区县 > 市 > 省）。
 *
 * 数据来源（公开可核验）：
 *   DataV.GeoAtlas 全国行政区划边界数据
 *   https://geo.datav.aliyun.com/areas_v3/bound/all.json
 *   该数据由阿里云 DataV 数据可视化平台维护，几何与中心点源自国家地理信息
 *   公共服务平台（天地图）行政区划边界，adcode 为国家统计局行政代码。
 *   抓取快照 sha256：
 *   bc41efe39693f3eaa1705ecc599a0c53797e186f4a39f30d522c594084a296a8
 *
 * 覆盖：34 省级（含港澳台）+ 363 地级 + 2840 区县，共 3237 条。
 *   直辖市（京津沪渝）、港澳台无「地级市」层，其区县直属省级（parent = 省级 code）。
 *
 * ⚠️ 数据体量约 347KB（gzip 约 67KB），因此走 **动态 import** 按需分包，
 *    只有打开项目表单时才会加载该 chunk，不拖慢首屏。
 */

export type DivisionLevel = 'province' | 'city' | 'district';

export interface Division {
  /** 国标 adcode（6 位） */
  code: string;
  name: string;
  level: DivisionLevel;
  /** 上级 adcode；省级的上级为 '100000'（中华人民共和国） */
  parent: string | null;
  /** 中心点经度 */
  lng: number;
  /** 中心点纬度 */
  lat: number;
}

interface RawDivision {
  code: string;
  name: string;
  level: string;
  parent: string | null;
  lng: number;
  lat: number;
}

let divisionsPromise: Promise<Division[]> | null = null;

/**
 * 加载全部行政区划（模块内缓存，仅首次触发网络 / chunk 加载）。
 */
export function loadDivisions(): Promise<Division[]> {
  if (!divisionsPromise) {
    divisionsPromise = import('./china-divisions.json').then((mod) => {
      const raw = (mod.default ?? mod) as RawDivision[];
      return raw
        .filter((d) => d.level === 'province' || d.level === 'city' || d.level === 'district')
        .map((d) => ({
          code: d.code,
          name: d.name,
          level: d.level as DivisionLevel,
          parent: d.parent,
          lng: d.lng,
          lat: d.lat,
        }));
    });
  }
  return divisionsPromise;
}

/** 按 adcode 取行政区（找不到返回 undefined） */
export function findDivision(list: readonly Division[], code: string): Division | undefined {
  if (!code) return undefined;
  return list.find((d) => d.code === code);
}

/** 按 adcode 取行政区名（找不到返回空串） */
export function divisionNameOf(list: readonly Division[], code: string): string {
  return findDivision(list, code)?.name ?? '';
}

/**
 * 坐标解析优先级：区县 > 市 > 省。
 * 传入的三者均为可选，全部缺失时返回 null。
 */
export function resolveCenter(
  province?: Division,
  city?: Division,
  district?: Division,
): [number, number] | null {
  const d = district ?? city ?? province;
  return d ? [d.lng, d.lat] : null;
}
